/** @jsx isolateComponent */

import React, { MouseEvent, useState } from 'react';

import {
  AnchorTarget,
  Animation,
  Avatar,
  Colors,
  FloatingBox,
  TooltipWrapper,
  Typography,
  kebabCaseAriaAttrs,
  useClickOutside,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { createUniqueClassName } from '../../utils/createUniqueClassName';

import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../../constants';
import type { UniversalNavLinkType } from '../../constants';

type Account = {
  id: number;
  name: string;
  imageUrl?: string;
  accountSwitcherUrl: string;
};

export type Props = {
  /**
   * List of user's accounts to render in the accounts menu.
   */
  accounts: Array<Account>;
  /**
   * Id of currently active user account.
   */
  currentAccountId?: number;
  /**
   * Email address of the currently logged in user.
   */
  email: string;
  /**
   * Changes the background color to white (#fff).
   */
  hasWhiteBackground?: boolean;
  /**
   * Label describing the accounts menu.
   */
  menuLabel: string;
  /**
   * Array of links to render in the accounts menu.
   */
  menuLinks: Array<UniversalNavLinkType>;
  /**
   * Handler called when an account menu item is clicked.
   */
  onAccountClick?: (account: Account, event: MouseEvent) => void;
  /**
   * How large the menu renders, especially the `Avatar`.
   */
  size: 'small' | 'medium';
};

const detailsClassName = createUniqueClassName('AccountsMenu', 'details');

const Styles = labelStyles('AccountsMenu', {
  root: {
    position: 'relative',
  },
  accounts: {
    borderBottom: `1px solid ${Colors.neutral300}`,
    marginBottom: 10,
    paddingBottom: 10,
    overflow: 'auto',
    // 60px is height of each account item in the list.
    // No need to share a variable for this yet.
    maxHeight: 'calc(60px * 3.5)',
  },
  summary: {
    position: 'relative',
    display: 'block',
    cursor: 'pointer',
    transition: Animation.transitionValue,
    '&::-webkit-details-marker': {
      display: 'none',
    },
  },
  menuContainer: {
    padding: 10,
    width: 300,
  },
  email: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 10px',
    ...Typography.smallPrint1Semibold,
    color: Colors.neutral600,
    marginBottom: 10,
  },
  emailText: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

// Renders a list of accounts as menu items
const renderAccountSwitchMenu = (
  props: Props,
  setIsMenuOpen: (isMenuOpen: boolean) => void
) => {
  return (
    <div css={Styles.accounts}>
      {props.accounts.map((account: Account) => {
        const isCurrentAccount = account.id === props.currentAccountId;
        return (
          <UniversalNavLink
            href={account.accountSwitcherUrl}
            isSelected={isCurrentAccount}
            key={account.id}
            onClick={(event) => {
              if (props.onAccountClick) {
                props.onAccountClick(account, event);
              }
              setIsMenuOpen(false);
            }}
            renderBeforeText={() => (
              <div css={Styles.avatar}>
                <Avatar
                  isBlock={true}
                  isSelected={isCurrentAccount}
                  name={account.name}
                  shouldLazilyLoad={false}
                  size={40}
                  url={account.imageUrl}
                />
              </div>
            )}
            role="menuitem"
            text={account.name}
          />
        );
      })}
    </div>
  );
};

const renderMenuLinks = (
  props: Props,
  setIsMenuOpen: (isMenuOpen: boolean) => void
) => {
  return props.menuLinks.map((menuLink) => (
    <UniversalNavLink
      href={menuLink.href}
      icon={menuLink.icon}
      key={menuLink.text}
      onClick={() => setIsMenuOpen(false)}
      role="menuitem"
      text={menuLink.text}
      target={menuLink.target as AnchorTarget}
    />
  ));
};

// This is fake placeholder account data for when no current account
// is found, which should only happen when loading accounts. This fallback
// account allows `AccountsMenu` to fully render so the UI is more
// incrementally updated instead of pieces of it conditionally rendering.
const fallbackAccount: Account = {
  id: -1,
  name: '⋯',
  accountSwitcherUrl: '/',
};

/**
 * @private
 * Renders a menu with accounts and account action links.
 */
const AccountsMenu = (props: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => setIsMenuOpen(false),
  });

  const currentAccount: Account =
    props.accounts.find((account) => account.id === props.currentAccountId) ||
    fallbackAccount;

  const toggleMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  // The native implementation of details handles keyboard events by default.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div css={Styles.root}>
      <details
        className={detailsClassName}
        data-testid="accountsMenu"
        open={isMenuOpen}
        ref={(node) => setClickOutsideNode(node)}
      >
        <summary
          aria-haspopup="menu"
          aria-label={props.menuLabel}
          css={Styles.summary}
          data-testid="accounts-menu-summary"
          onClick={toggleMenu}
          tabIndex={0}
        >
          <TooltipWrapper
            content={currentAccount.name}
            isBlock={true}
            position="west"
            shouldCorrectPosition={false}
            tooltipId="tooltip-accounts-menu"
          >
            {({ childProps }) => (
              <div {...kebabCaseAriaAttrs(childProps)}>
                <Avatar
                  isBlock={true}
                  isInteractive={true}
                  isSelected={isMenuOpen}
                  name={currentAccount.name}
                  shouldLazilyLoad={false}
                  size={props.size === 'small' ? 25 : 40}
                  url={currentAccount.imageUrl}
                />
              </div>
            )}
          </TooltipWrapper>
        </summary>
        <FloatingBox
          align="right"
          hasWhiteBackground={props.hasWhiteBackground}
          position="south"
          borderRadius={10}
        >
          <div css={Styles.menuContainer} role="menu">
            <div css={Styles.email}>
              <span css={Styles.emailText}>{props.email}</span>
            </div>
            {props.accounts.length > 1 &&
              renderAccountSwitchMenu(props, setIsMenuOpen)}
            {renderMenuLinks(props, setIsMenuOpen)}
          </div>
        </FloatingBox>
      </details>
    </div>
  );
};

AccountsMenu.defaultProps = {
  menuLabel: 'Accounts Settings',
  menuLinks: AccountsMenuLinks,
  size: 'medium',
};

export default AccountsMenu;
